import React from "react";
import { useLang } from "../i18n";
import { IMAGES } from "../data/services";
import { Seo } from "../components/site/Seo";
import { MaskLine, FadeUp } from "../components/site/Reveal";
import { CTA } from "../components/site/CTA";
import { ShieldCheck, Plane, Cpu, FileText, Award, Users, Camera, Radar } from "lucide-react";

export default function Expertise() {
  const { lang } = useLang();
  const t = {
    fr: {
      tag: "À propos & Expertise", title: "La confiance se prouve.",
      lead: "Fixedrone met une expertise certifiée au service de vos projets. Voici les éléments qui garantissent la qualité et la conformité de nos interventions.",
      certT: "Certifications & autorisations", certLead: "Nos habilitations et conformité réglementaire.",
      cert: [
        { i: ShieldCheck, t: "Certification de télépilote", d: "[À compléter : catégorie Ouverte / Spécifique EASA, n° de brevet]" },
        { i: Award, t: "Déclaration d'exploitant", d: "[À compléter : n° d'exploitant UAS, pays d'enregistrement]" },
        { i: Plane, t: "Autorisations de vol", d: "[À compléter : scénarios STS, autorisations spécifiques, zones]" },
        { i: FileText, t: "Assurance responsabilité civile", d: "[À compléter : assureur et montant de couverture]" },
      ],
      matT: "Drones & capteurs", matLead: "Un parc adapté à chaque mission.",
      mat: [
        { i: Plane, t: "Drones", d: "[À compléter : modèles, ex. DJI Matrice / Mavic Enterprise]" },
        { i: Camera, t: "Caméras & optiques", d: "[À compléter : RGB haute résolution, zoom optique]" },
        { i: Radar, t: "Capteurs spécialisés", d: "[À compléter : thermique radiométrique, multispectral, LiDAR]" },
        { i: Cpu, t: "Précision", d: "[À compléter : GPS RTK/PPK, précision centimétrique]" },
      ],
      procT: "Notre méthode", proc: [
        { n: "01", t: "Cadrage du besoin", d: "Analyse de votre objectif, du site et des contraintes réglementaires." },
        { n: "02", t: "Vol & acquisition", d: "Plan de vol optimisé, captation sécurisée par pilote certifié." },
        { n: "03", t: "Traitement des données", d: "Photogrammétrie, thermographie, modélisation 3D et mesures." },
        { n: "04", t: "Rapport & livraison", d: "Livrables annotés et exploitables, restitués rapidement." },
      ],
      refT: "Ils nous font confiance", refLead: "[À compléter : logos et références clients, secteurs BTP, énergie, agriculture…]",
    },
    en: {
      tag: "About & Expertise", title: "Trust must be proven.",
      lead: "Fixedrone puts certified expertise at the service of your projects. Here are the elements guaranteeing the quality and compliance of our operations.",
      certT: "Certifications & authorizations", certLead: "Our credentials and regulatory compliance.",
      cert: [
        { i: ShieldCheck, t: "Remote pilot certification", d: "[To complete: EASA Open/Specific category, license no.]" },
        { i: Award, t: "Operator declaration", d: "[To complete: UAS operator no., country of registration]" },
        { i: Plane, t: "Flight authorizations", d: "[To complete: STS scenarios, specific authorizations, zones]" },
        { i: FileText, t: "Liability insurance", d: "[To complete: insurer and coverage amount]" },
      ],
      matT: "Drones & sensors", matLead: "A fleet suited to every mission.",
      mat: [
        { i: Plane, t: "Drones", d: "[To complete: models, e.g. DJI Matrice / Mavic Enterprise]" },
        { i: Camera, t: "Cameras & optics", d: "[To complete: high-res RGB, optical zoom]" },
        { i: Radar, t: "Specialized sensors", d: "[To complete: radiometric thermal, multispectral, LiDAR]" },
        { i: Cpu, t: "Accuracy", d: "[To complete: RTK/PPK GPS, centimeter accuracy]" },
      ],
      procT: "Our method", proc: [
        { n: "01", t: "Scoping", d: "Analysis of your goal, the site and regulatory constraints." },
        { n: "02", t: "Flight & capture", d: "Optimized flight plan, safe capture by a certified pilot." },
        { n: "03", t: "Data processing", d: "Photogrammetry, thermography, 3D modeling and measurements." },
        { n: "04", t: "Report & delivery", d: "Annotated, actionable deliverables, delivered quickly." },
      ],
      refT: "They trust us", refLead: "[To complete: client logos and references — construction, energy, agriculture…]",
    },
  }[lang];

  const Grid = ({ title, lead, items }) => (
    <section className="py-20 md:py-28 bg-[#050505] border-t border-white/10">
      <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
        <FadeUp><span className="font-mono-p text-xs uppercase tracking-[0.25em] text-primary-c">{title}</span></FadeUp>
        {lead && <FadeUp delay={0.05}><p className="text-white/55 mt-3 max-w-2xl">{lead}</p></FadeUp>}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {items.map((it, i) => (
            <FadeUp key={i} delay={i * 0.05}>
              <div className="bg-[#0a0a0c] p-8 h-full min-h-[220px] flex flex-col">
                <it.i className="text-primary-c" size={24} />
                <h3 className="font-display font-semibold text-lg mt-5">{it.t}</h3>
                <p className="text-white/45 text-sm mt-3 leading-relaxed">{it.d}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );

  return (
    <>
      <Seo
        title={lang === "fr" ? "Expertise, certifications & matériel drone | Fixedrone Luxembourg" : "Expertise, certifications & drone equipment | Fixedrone Luxembourg"}
        description={lang === "fr" ? "Découvrez l'expertise Fixedrone : certifications de télépilote, autorisations de vol, drones et capteurs (thermique, multispectral, RTK/LiDAR), méthode de travail et références clients." : "Discover Fixedrone expertise: remote pilot certifications, flight authorizations, drones and sensors (thermal, multispectral, RTK/LiDAR), work method and client references."}
        keywords="expertise drone, certification télépilote EASA, autorisation de vol drone, drone RTK, caméra thermique, LiDAR, Luxembourg"
        path="/expertise"
      />
      <section className="pt-40 pb-16 md:pb-20 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 z-0"><img src={IMAGES.drone} alt="Drone professionnel Fixedrone" className="w-full h-full object-cover opacity-25" /><div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" /></div>
        <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <FadeUp><span className="font-mono-p text-xs uppercase tracking-[0.25em] text-primary-c">{t.tag}</span></FadeUp>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.9] mt-6 max-w-3xl"><MaskLine load>{t.title}</MaskLine></h1>
          <FadeUp delay={0.1}><p className="text-white/65 mt-6 max-w-2xl text-lg leading-relaxed">{t.lead}</p></FadeUp>
          <FadeUp delay={0.15}><p className="mt-6 font-mono-p text-xs text-white/40 border border-dashed border-white/15 rounded-lg px-4 py-3 inline-block">ℹ️ {lang === "fr" ? "Les champs entre crochets [ ] sont des espaces réservés à compléter avec vos informations réelles." : "Bracketed [ ] fields are placeholders to complete with your real information."}</p></FadeUp>
        </div>
      </section>

      <Grid title={t.certT} lead={t.certLead} items={t.cert} />
      <Grid title={t.matT} lead={t.matLead} items={t.mat} />

      {/* Method */}
      <section className="py-20 md:py-28 bg-[#070708] border-t border-white/10">
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <FadeUp><span className="font-mono-p text-xs uppercase tracking-[0.25em] text-primary-c">{t.procT}</span></FadeUp>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.proc.map((p, i) => (
              <FadeUp key={i} delay={i * 0.05}>
                <div className="border-t-2 border-[var(--primary)] pt-6">
                  <span className="font-display font-black text-4xl text-white/20">{p.n}</span>
                  <h3 className="font-display font-semibold text-xl mt-3">{p.t}</h3>
                  <p className="text-white/55 text-sm mt-2 leading-relaxed">{p.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* References */}
      <section className="py-20 md:py-28 bg-[#050505] border-t border-white/10">
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <FadeUp><span className="font-mono-p text-xs uppercase tracking-[0.25em] text-primary-c flex items-center justify-center gap-2"><Users size={14} /> {t.refT}</span></FadeUp>
          <FadeUp delay={0.05}><p className="text-white/45 mt-5 max-w-xl mx-auto">{t.refLead}</p></FadeUp>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-24 border border-dashed border-white/15 rounded-xl flex items-center justify-center font-mono-p text-xs text-white/30">Logo client</div>
            ))}
          </div>
        </div>
      </section>
      <CTA />
    </>
  );
}
